import { resolveCustomerAddressFields, toPostalAddress } from '@klareospace/pharos-lib-front-utils';
import type { ResolvedCustomerAddressFields } from '@klareospace/pharos-lib-front-utils';
import {
  buildCustomerDefaultContact,
  formatPostalAddress,
  isSameContact,
  keepDefined,
  normalizeCountry,
  normalizeCountryCode,
  ratioToPercent,
} from '@klareospace/pharos-lib-utils';
import type {
  Contact,
  CreateCustomer,
  Customer,
  CustomerAddress,
  CustomerAddressLocation,
  GeoCoordinates,
  PhoneNumber,
  PostalAddress,
} from '@klareospace/pharos-lib-types';
import { GEOCODING_SOURCE } from '@klareospace/pharos-lib-types';

import type { AddressBookEntryInput, AddressFormInput, CustomerFormInput } from '../schemas/customer.schema';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CreateScope {
  siteId: string | null;
  spaceId: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_ADDRESS: AddressFormInput = {
  additionalInformations: '',
  city: '',
  complement: '',
  country: '',
  digicode: '',
  floor: '',
  hasElevator: false,
  houseNumber: '',
  postalCode: '',
  street: '',
};

/**
 * Postal identity fields that lock a customer address against future API-driven overrides once
 * edited from the front office (PHA-364). Kept in sync with buildAddress()'s output shape.
 */
const PROTECTED_ADDRESS_FIELDS = ['houseNumber', 'street', 'postalCode', 'city', 'country'] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** A blank, unsaved address-book entry — used for a new empty add form and for create mode. */
export function buildEmptyAddressEntry(): AddressBookEntryInput {
  return {
    address: EMPTY_ADDRESS,
    contactName: '',
    contactPhone: null,
    coordinates: { latitude: undefined, longitude: undefined },
    geocodingRawScore: undefined,
    geocodingScore: undefined,
    geocodingSource: undefined,
    isCustomerContact: true,
    isDefault: true,
    sourceIndex: undefined,
  };
}

/**
 * Structural param shapes: these checks are also called from the Zod `superRefine`, whose
 * inferred entry has all-optional address fields.
 */
interface AddressDataFields {
  city?: string;
  postalCode?: string;
  street?: string;
}

export function hasAddressData(entry: { address: AddressDataFields }): boolean {
  return !!(entry.address.city?.trim() || entry.address.street?.trim() || entry.address.postalCode?.trim());
}

interface AddressCompletenessEntry {
  address: { city?: string; country?: string };
  coordinates?: { latitude?: number; longitude?: number };
}

/**
 * True when an address-book entry carries enough data to satisfy the backend's `LocationDTO`
 * contract (`PostalAddressDTO` requires non-empty `city`/`country`, `LocationDTO` requires
 * `coordinates`) — mirrored here so an incomplete entry is caught client-side instead of
 * surfacing as a 400 on save.
 */
export function isAddressEntryComplete(entry: AddressCompletenessEntry): boolean {
  const hasCity = !!entry.address.city?.trim();
  const hasCountry = !!entry.address.country?.trim();
  const hasCoordinates = entry.coordinates?.latitude !== undefined && entry.coordinates?.longitude !== undefined;
  return hasCity && hasCountry && hasCoordinates;
}

export function buildAddress(entry: AddressBookEntryInput): AddressFormInput {
  const { address } = entry;
  return {
    additionalInformations: address.additionalInformations,
    city: address.city ?? '',
    complement: address.complement,
    country: address.country ?? '',
    digicode: address.digicode,
    floor: address.floor,
    hasElevator: address.hasElevator,
    houseNumber: address.houseNumber,
    postalCode: address.postalCode ?? '',
    street: address.street,
  };
}

/** The customer's own phone is always required by the form schema. */
function buildPhone(data: CustomerFormInput): PhoneNumber {
  return { dialCode: data.phone.dialCode, number: data.phone.number.trim() };
}

export function buildOptionalCustomerFields(
  data: CustomerFormInput,
): Pick<CreateCustomer, 'companyName' | 'email' | 'loyaltyNumber'> {
  return keepDefined({
    companyName: data.companyName?.trim() || undefined,
    email: data.email?.trim() || undefined,
    loyaltyNumber: data.loyaltyNumber?.trim() || undefined,
  });
}

/** Country is compared format-tolerantly (normalized on both sides) — "France" vs "FRA" is not a change. */
function protectedFieldMatches(
  field: (typeof PROTECTED_ADDRESS_FIELDS)[number],
  formValue: string,
  existingValue: string,
): boolean {
  if (field === 'country') {
    return normalizeCountry(formValue) === normalizeCountry(existingValue);
  }
  return formValue === existingValue;
}

/** Identity-only subset of hasProtectedFieldChanged, used to key originalAddress preservation. */
function hasIdentityFieldChanged(addressValues: AddressFormInput, existing: ResolvedCustomerAddressFields): boolean {
  return PROTECTED_ADDRESS_FIELDS.some(
    (field) => !protectedFieldMatches(field, addressValues[field] ?? '', existing.address?.[field] ?? ''),
  );
}

/**
 * Detects whether any protected identity field (houseNumber, street, postalCode, city, country)
 * or the coordinates differ from the entry's existing source address. Any such change means the
 * address was edited by the front-office user and must be stamped `source: GEOCODING_SOURCE.FRONT_OFFICE`,
 * locking it against future API-driven overrides (PHA-364).
 */
function hasProtectedFieldChanged(
  entry: AddressBookEntryInput,
  addressValues: AddressFormInput,
  existing: ResolvedCustomerAddressFields,
): boolean {
  if (hasIdentityFieldChanged(addressValues, existing)) return true;
  return (
    entry.coordinates?.latitude !== existing.coordinates?.latitude ||
    entry.coordinates?.longitude !== existing.coordinates?.longitude
  );
}

/**
 * True when the incoming edit carries neither a protected field change nor a this-session
 * geocode/autocomplete result, and existing coordinates are available to carry over as-is.
 * Extracted from buildNewCoordinates() to keep it under the cyclomatic complexity limit.
 */
function canCarryOverExistingCoordinates(
  entry: AddressBookEntryInput,
  addressValues: AddressFormInput,
  existing: ResolvedCustomerAddressFields,
): boolean {
  if (entry.geocodingRawScore !== undefined) return false;
  if (hasProtectedFieldChanged(entry, addressValues, existing)) return false;
  return existing.coordinates !== undefined;
}

/**
 * Builds new GeoCoordinates from the entry's form data.
 *
 * Returns undefined when latitude or longitude is missing (nothing to persist).
 *
 * Source (PHA-364): stamped `GEOCODING_SOURCE.FRONT_OFFICE` when a protected field changed this
 * edit, or a geocode/autocomplete result was produced this session (`geocodingRawScore` set).
 * Otherwise the existing stored coordinates (source AND score) are returned untouched.
 *
 * Score: the raw 0–1 score produced by this session's geocode/autocomplete result when present;
 * otherwise the existing stored score; otherwise 1 (brand-new address, manually typed coordinates).
 */
function buildNewCoordinates(
  entry: AddressBookEntryInput,
  addressValues: AddressFormInput,
  existing: ResolvedCustomerAddressFields,
): GeoCoordinates | undefined {
  const latitude = entry.coordinates?.latitude;
  const longitude = entry.coordinates?.longitude;
  if (latitude === undefined || longitude === undefined) return undefined;

  if (canCarryOverExistingCoordinates(entry, addressValues, existing)) {
    return existing.coordinates;
  }

  const score = entry.geocodingRawScore ?? existing.coordinates?.score ?? 1;
  return { latitude, longitude, score, source: GEOCODING_SOURCE.FRONT_OFFICE };
}

/**
 * Builds the address contact override from the entry's form data. Presence-based override: when
 * the customer is the contact (`isCustomerContact: true`, the default), no `contact` is stored on
 * the address at all — the customer's own name/phone is the implicit contact. Only when the toggle
 * is OFF do we build and persist an explicit `contact` from the dedicated `contactName`/
 * `contactPhone` fields.
 */
export function buildOverrideContact(entry: AddressBookEntryInput): Contact | undefined {
  if (entry.isCustomerContact) return undefined;
  const name = entry.contactName?.trim();
  const number = entry.contactPhone?.number?.trim();
  if (!name || !number || !entry.contactPhone) return undefined;
  return { name, phone: { dialCode: entry.contactPhone.dialCode, number } };
}

/**
 * Builds a CustomerAddressLocation from a PostalAddress and optional coordinates.
 * When coordinates are present the returned value is fully type-safe.
 * When they are absent, a cast is necessary because CustomerAddressLocation extends Location
 * which mandates coordinates, while the domain allows address-only locations.
 */
function buildCustomerAddressLocation(
  address: PostalAddress,
  coordinates: GeoCoordinates | undefined,
  isDefault: boolean,
): CustomerAddressLocation {
  if (coordinates !== undefined) {
    return { address, coordinates, isDefault };
  }
  return { address, isDefault } as unknown as CustomerAddressLocation;
}

/**
 * originalAddress is the backend's repeat-order identity key: preserved on a no-op identity
 * save, recomputed only on a genuine identity edit or when none is stored (legacy).
 */
function resolveOriginalAddress(
  addressValues: AddressFormInput,
  existingSourceAddress: ResolvedCustomerAddressFields,
  source: CustomerAddress | undefined,
  postalAddress: PostalAddress,
): string {
  const storedOriginalAddress = source?.originalAddress;
  if (storedOriginalAddress && !hasIdentityFieldChanged(addressValues, existingSourceAddress)) {
    return storedOriginalAddress;
  }
  return formatPostalAddress(postalAddress);
}

/**
 * Builds a single `CustomerAddress` from one address-book entry, resolving its source address (if
 * any) via `sourceIndex` and applying the protected-field and `originalAddress` preservation rules
 * against that specific source — not just the first address. `entry.isDefault` is written as-is
 * here; the invariant "exactly one default" is enforced afterward by `normalizeDefaultAddresses`
 * across the whole built list.
 */
export function buildAddressEntry(entry: AddressBookEntryInput, existing: CustomerAddress[]): CustomerAddress {
  const addressValues = buildAddress(entry);
  const postalAddress = toPostalAddress({ ...addressValues, country: normalizeCountry(addressValues.country) });
  const source = entry.sourceIndex !== undefined ? existing[entry.sourceIndex] : undefined;
  const existingSourceAddress = resolveCustomerAddressFields(source);
  const originalAddress = resolveOriginalAddress(addressValues, existingSourceAddress, source, postalAddress);
  const newCoords = buildNewCoordinates(entry, addressValues, existingSourceAddress);
  const coordinates = newCoords ?? existingSourceAddress.coordinates;
  const contact = buildOverrideContact(entry);
  const location = buildCustomerAddressLocation(postalAddress, coordinates, entry.isDefault);

  return {
    ...(contact !== undefined ? { contact } : {}),
    location,
    originalAddress,
  };
}

/**
 * Index of the address flagged default, or `0` (the first entry) when none is flagged. Resolved
 * through `resolveCustomerAddressFields` (not a direct `address.location.isDefault` dereference)
 * so a legacy, pre-PHA-363 flat-shaped address (no `location` wrapper) is tolerated instead of
 * throwing.
 */
export function findDefaultAddressIndex(addresses: CustomerAddress[]): number {
  const defaultIndex = addresses.findIndex((address) => resolveCustomerAddressFields(address).isDefault === true);
  return defaultIndex === -1 ? 0 : defaultIndex;
}

/**
 * Enforces "exactly one default address whenever addresses exist" on the built list (mirrors the
 * backend's own `normalizeDefaultAddress`): the first flagged entry (if any) stays the
 * sole default; when none is flagged (e.g. the flagged entry was an empty draft dropped by
 * buildAddresses), the first entry becomes the default.
 */
export function normalizeDefaultAddresses(addresses: CustomerAddress[]): CustomerAddress[] {
  if (addresses.length === 0) return addresses;
  const resolvedDefaultIndex = findDefaultAddressIndex(addresses);
  return addresses.map((address, index) => ({
    ...address,
    location: { ...address.location, isDefault: index === resolvedDefaultIndex },
  }));
}

/**
 * Resolves the contact-override toggle defaults from a stored address, tolerant of pre-migration
 * data auto-materialized before the override became optional. No stored contact, or a stored
 * contact equal to the customer's own default (name + phone), ⇒ toggle ON (customer is the
 * contact). Otherwise ⇒ toggle OFF, prefilled from the override.
 */
function resolveContactDefaults(
  storedAddress: CustomerAddress | undefined,
  customer: Customer,
): Pick<AddressBookEntryInput, 'contactName' | 'contactPhone' | 'isCustomerContact'> {
  const storedContact: Contact | undefined = storedAddress?.contact;
  const isOverride =
    storedContact !== undefined && !isSameContact(storedContact, buildCustomerDefaultContact(customer));

  if (!isOverride) {
    return { contactName: '', contactPhone: null, isCustomerContact: true };
  }
  return {
    contactName: storedContact.name,
    contactPhone: storedContact.phone ?? null,
    isCustomerContact: false,
  };
}

/** Converts a stored geocoding score (0-1) to the form-state percentage (0-100). Undefined when no score is stored. */
function resolveGeocodingScore(score: number | undefined): number | undefined {
  if (score === undefined) return undefined;
  return ratioToPercent(score, 0);
}

/**
 * Resolves one stored address's postal fields into `AddressFormInput` defaults. `hasElevator` is
 * assigned last (not through `keepDefined` with the rest) so it stays `undefined` — not
 * `EMPTY_ADDRESS`'s `false` — when the stored address never specified it, so a complement-parts
 * display built from this entry only shows the elevator chip for addresses that actually have an
 * answer, exactly like it does for a `Site`'s own stored address.
 */
function resolveAddressDefaults(address: Partial<PostalAddress> | undefined): AddressFormInput {
  if (!address) return EMPTY_ADDRESS;
  return {
    ...EMPTY_ADDRESS,
    ...keepDefined({
      additionalInformations: address.additionalInformations,
      city: address.city,
      complement: address.complement,
      country: normalizeCountryCode(address.country),
      digicode: address.digicode,
      floor: address.floor,
      houseNumber: address.houseNumber,
      postalCode: address.postalCode,
      street: address.street,
    }),
    hasElevator: address.hasElevator,
  };
}

/**
 * Maps one stored CustomerAddress into an AddressBookEntryInput, tolerant of both the current
 * shape and the legacy flat shape (via `resolveCustomerAddressFields`). `index` is recorded as
 * `sourceIndex` so `buildPayload` can key its address-identity protection back to this exact
 * stored address regardless of the entry's later position in the array.
 */
export function buildAddressEntryFromCustomerAddress(
  storedAddress: CustomerAddress,
  index: number,
  customer: Customer,
): AddressBookEntryInput {
  const { address, coordinates, isDefault } = resolveCustomerAddressFields(storedAddress);
  return {
    address: resolveAddressDefaults(address),
    coordinates: { latitude: coordinates?.latitude, longitude: coordinates?.longitude },
    // geocodingRawScore and geocodingSource are intentionally left undefined — they are managed
    // by AddressInput via initialAddress and onSetValue callbacks, populated only when a
    // geocode/autocomplete interaction happens this session.
    geocodingRawScore: undefined,
    geocodingScore: resolveGeocodingScore(coordinates?.score),
    geocodingSource: undefined,
    isDefault: isDefault ?? false,
    sourceIndex: index,
    ...resolveContactDefaults(storedAddress, customer),
  };
}

/**
 * Maps every address-book entry to a persisted CustomerAddress, dropping entries with no postal
 * data (an untouched empty add form), and normalizing the default flag across the result.
 */
function buildAddresses(data: CustomerFormInput, existing: CustomerAddress[]): CustomerAddress[] {
  const built = data.addresses.filter(hasAddressData).map((entry) => buildAddressEntry(entry, existing));
  return normalizeDefaultAddresses(built);
}

export function buildPayload(
  data: CustomerFormInput,
  existingCustomer?: Customer,
  scope?: CreateScope,
): CreateCustomer {
  const scopeFields = buildScopeFields(existingCustomer, scope);
  return {
    addresses: buildAddresses(data, existingCustomer?.addresses ?? []),
    firstName: data.firstName,
    lastName: data.lastName,
    phone: buildPhone(data),
    type: data.type,
    ...scopeFields,
    ...buildOptionalCustomerFields(data),
  };
}

function buildScopeFields(
  existingCustomer: Customer | undefined,
  scope: CreateScope | undefined,
): Pick<CreateCustomer, 'siteIds' | 'spaceId'> {
  if (existingCustomer) {
    // Edit mode: preserve the existing siteIds; never touch spaceId.
    return { siteIds: existingCustomer.siteIds };
  }
  // Create mode: derive scope from the master context selector.
  if (scope?.siteId) {
    // Include the parent spaceId so the backend can also apply the space-level clause.
    return { siteIds: [scope.siteId], spaceId: scope.spaceId ?? undefined };
  }
  if (scope?.spaceId) {
    return { siteIds: [], spaceId: scope.spaceId };
  }
  return { siteIds: [] };
}
